import { DownloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Modal, Spin, message } from 'antd';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import './MarkdownViewerModal.css';
import moment from 'moment';
import { useSelector } from 'react-redux';

interface MarkdownViewerModalProps {
  visible: boolean;
  onCancel: () => void;
  fileUrl: string;
  id: string;
  title?: string;
  receiverName?: string;
  exportDeepResearchDocument: (id: string, type: string, fileName: string) => void;
}

export const MarkdownViewerModal: React.FC<MarkdownViewerModalProps> = ({
  visible,
  onCancel,
  fileUrl,
  id,
  title = 'Research Document',
  receiverName = 'unknown',
  exportDeepResearchDocument,
}) => {

  const { exportDocumentLoading } = useSelector((state: any) => state.deepResearch);
  const fileName = `deep_research_${receiverName}_${moment().format('YYYY-MM-DD_HH-mm-ss')}`

  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && fileUrl) {
      fetchMarkdownContent();
    }
  }, [visible, fileUrl]);

  const fetchMarkdownContent = async () => {
    if (!fileUrl) return;

    setLoading(true);
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const content = await response.text();

      // Check if content is empty or not markdown-like
      if (!content || content.trim().length === 0) {
        throw new Error('Document content is empty');
      }

      setMarkdownContent(content);
    } catch (error) {
      console.error('Error fetching markdown content:', error);
      if (error instanceof Error && error.message.includes('empty')) {
        message.error('The document appears to be empty or invalid.');
      } else {
        message.error('Failed to load the document. Please try again.');
      }
      setMarkdownContent('');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setMarkdownContent('');
    onCancel();
  };

  const handleDownload = async (type: 'md' | 'pdf') => {
    
    try {
      if (!fileUrl) return;

      setIsDownloading(true);

      if (type === 'pdf') {
        exportDeepResearchDocument(id, type, fileName);
        return;
      }

      if (type === 'md') {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${fileName}.md`;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }

      message.success('Download completed successfully!');
    } catch (error) {
      console.error('Error downloading file:', error);
      message.error('Failed to download the document. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const customComponents = {
    // Handle anchor tags properly
    a: ({ node, href, children, ...props }) => {
      // If it's an internal link (starts with #)
      if (href && href.startsWith('#')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              const targetId = href.substring(1);
              const targetElement = document.getElementById(targetId);
              if (targetElement) {
                targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }}
            className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
            {...props}
          >
            {children}
          </a>
        );
      }

      // For anchor tags with id (like <a id="section-1"></a>)
      if (props.id && !href) {
        return (
          <span
            id={props.id}
            className="block"
            style={{ scrollMarginTop: '20px' }}
            {...props}
          >
            {children}
          </span>
        );
      }

      // Regular external links
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
          {...props}
        >
          {children}
        </a>
      );
    },
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', marginRight: '20px' }}>
          <span>{title}</span>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Dropdown
              disabled={isDownloading || exportDocumentLoading}
              menu={{
                items: [
                  { label: 'Download as MD', key: 'md', onClick: () => handleDownload('md') },
                  { label: 'Download as PDF', key: 'pdf', onClick: () => handleDownload('pdf') },
                ],
              }}
              trigger={['click']}
            >
              <Button type="link" icon={isDownloading || exportDocumentLoading ? <Spin size="small" /> : <DownloadOutlined />} size="small" disabled={!fileUrl} title="Download document" style={{ padding: 0 }}>
                Download
              </Button>
            </Dropdown>

          </div>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      style={{ top: 20 }}
      destroyOnClose
    >
      <div className="markdown-viewer-content">
        {loading ? (
          <div className="markdown-loading">
            <Spin size="large" />
            <p>Loading document...</p>
          </div>
        ) : markdownContent ? (
          <div>
            <div className="markdown-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={customComponents}
              >
                {markdownContent}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="markdown-error">
            <p>No content to display</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
